/**
 * Imports
 *
 * Import any modules or components here.
 */
import {
    appConfigDev,
    appConfigKey,
    appConfigProd,
    appPayMethod,
    appDomain,
    claimForm,
    claimMethod,
    i18n,
    outputDiv,
    reCAPTCHAv3,
    userLanguage,
} from "commonAssets/js/constants";
import { createElement } from "commonAssets/lib/createElement";
import { formatPhone } from "commonAssets/lib/formatPhone";
import { toggleFieldAttributeV2 } from "commonAssets/lib/toggleFieldAttributeV2";
import { toggleForeignPhone } from "commonAssets/lib/toggleForeignPhone";
import { loadingGif } from "commonAssets/lib/loadingGif";
import { DigitalDisbursements } from "commonAssets/components/FormBase/DigitalDisbursements";
import {
    FormFields,
    FormFieldsLabel,
} from "commonAssets/components/FormBase/FormFields";
import { resolveHtmlFields } from "commonAssets/lib/resolveHtmlFields";

/**
 * Represents a class for returning the claim form
 * @class
 */
export class FormBase {
    constructor(data, language) {
        this.data = data;
        this.lang = language;
        this.config = {
            apiKey: appConfigKey,
            url: this.environment(),
        };
        this.claim = {
            // this is the claim object and for customInitClaim() function
            api: apigClientFactory.newClient(this.config),
            form: claimForm,
            inputs: {
                contact: {},
                fields: null,
                transactions: null,
                w9: null,
            },
            uploads: [],
            flags: {
                valid: true,
                sent: false,
                success: true,
                method: claimMethod,
            },
            data: {
                domain: this.hostname(),
                contact: {},
                fields: {},
                transactions: {},
                w9: {},
                files: [],
            },
            class_member: {},
        };
        this.exclusion = {
            api: apigClientFactory.newClient(this.config),
            inputs: {},
            valid: true,
            sent: false,
            data: {
                domain: this.hostname(),
                transactions: {},
            },
        };
    }

    /**
     * Return a deep-cloned structure where any `*_html` helper keys override
     * their corresponding base keys. If a helper value looks like a relative
     * path, it is fetched relative to the provided base URL (defaults to the
     * current page).
     *
     * @param {*} node
     * @param {string} [baseUrl]
     * @returns {Promise<*>}
     */
    static async resolveHtmlFields(node, { baseUrl, assets } = {}) {
        const fallbackUrl =
            baseUrl ||
            (typeof window !== "undefined" ? window.location.href : undefined);
        return resolveHtmlFields(node, {
            baseUrl: fallbackUrl,
            assets,
        });
    }

    /**
     * Get the hostname of the current window.
     * If the hostname consists of only one part (e.g., 'example'), it adds 'www' as the subdomain.
     *
     * @returns {string} The hostname of the current window.
     */
    hostname() {
        let hostname = window.location.hostname;
        if (hostname.split(".").length === 2) {
            hostname = `www.${hostname}`;
        }
        // TEMP (local testing, do not commit): act as production — the
        // production domain is sent and environment() picks appConfigProd.
        hostname = hostname.replace(/^local\./, "www.");
        return hostname;
    }

    environment() {
        const hostname = this.hostname();
        if (
            hostname.startsWith("local") ||
            hostname === "localhost" ||
            hostname.split(".")[1] === "angeiontest"
        ) {
            return appConfigDev;
        }
        return appConfigProd;
    }

    guard(params, getDeadline) {
        let form;
        if (params.form_options.generic_claim.display) {
            // If the Guard allows Generic Claims
            form = createElement(
                "form",
                {
                    class: "needs-validation",
                    id: "guard",
                    method: "post",
                },
                [
                    createElement(
                        "div",
                        { class: "row fs-5", id: "guardRow" },
                        [
                            this.notice_confirmation(params),
                            this.notice_generic(params),
                        ]
                    ),
                    this.claim_deadline(params, getDeadline),
                ]
            );
        } else {
            // For closed classes
            form = createElement(
                "form",
                {
                    class: "needs-validation",
                    id: "guard",
                    method: "post",
                },
                [
                    createElement(
                        "div",
                        { class: "row fs-5", id: "guardRow" },
                        [
                            this.notice_confirmation(
                                params,
                                "col-md-6 offset-md-3 mb-5"
                            ),
                        ]
                    ),
                    this.claim_deadline(params, getDeadline),
                ]
            );
        }
        return form;
    }

    notice_confirmation(params, additionalParams) {
        const noticeID = createElement(
            "fieldset",
            {
                class: additionalParams ? additionalParams : "col-md-6 mb-5",
                id: "notice-confirmation",
            },
            [
                createElement(
                    "div",
                    {
                        class: "bg-light text-dark border rounded p-3",
                    },
                    [
                        createElement(
                            "p",
                            { class: "mb-3" },
                            [],
                            params.form_options.notice_confirmation.body
                        ),
                        createElement("div", { class: "mb-3" }, [
                            createElement("input", {
                                type: "text",
                                name: "zero_zero_id",
                                class: "form-control",
                                id: "zero_zero_id",
                                "data-validate": "required",
                                required: "",
                            }),
                            createElement(
                                "label",
                                { for: "zero_zero_id" },
                                [],
                                params.form_options.notice_confirmation
                                    .zero_zero_id +
                                    ' <span class="error"></span>'
                            ),
                        ]),
                        createElement("div", { class: "mb-3" }, [
                            createElement("input", {
                                type: "text",
                                name: "confirmation_code",
                                class: "form-control",
                                id: "confirmation_code",
                                "data-validate": "required",
                                required: "",
                            }),
                            createElement(
                                "label",
                                { for: "confirmation_code" },
                                [],
                                params.form_options.notice_confirmation
                                    .confirmation_code +
                                    ' <span class="error"></span>'
                            ),
                        ]),
                        createElement("div", {}, [
                            createElement("input", {
                                type: "submit",
                                class: "btn btn-primary",
                                id: "submit-guard",
                                value: params.form_options.notice_confirmation
                                    .submit_guard,
                            }),
                        ]),
                    ]
                ),
            ]
        );
        return noticeID;
    }

    notice_generic(params) {
        const genericID = createElement(
            "fieldset",
            {
                class: "col-md-6 mb-5",
                id: "notice-general",
            },
            [
                createElement(
                    "div",
                    { class: "bg-light text-dark border rounded p-3" },
                    [
                        createElement(
                            "p",
                            [],
                            [],
                            params.form_options.generic_claim.body
                        ),
                        createElement("input", {
                            type: "button",
                            class: "btn btn-primary",
                            id: "skip-guard",
                            value: params.form_options.generic_claim.submit,
                            "aria-label":
                                params.form_options.generic_claim.submit,
                        }),
                    ]
                ),
            ]
        );
        return genericID;
    }

    claim_deadline(params, additionalParams) {
        // id="deadline_datetime" handling
        const alert = `<p class="mb-0">${params.form_options.claim_deadline_alert.body}</p>`;
        const parser = new DOMParser();
        const string = parser.parseFromString(alert, "text/html");

        const date_string = string.querySelector("span");
        date_string.textContent = additionalParams;

        const div = createElement(
            "div",
            {
                class: "alert alert-warning my-3 text-center",
                id: "claim_form_alert",
            },
            [],
            string.body.innerHTML
        );

        return div;
    }

    /**
     * Creates a claim form element with the given parameters and additional parameters.
     *
     * @param {Object} params - The form parameters.
     * @param {Object} additionalParams - Additional parameters for the form.
     * @returns {HTMLElement} - The created form element.
     */
    claim_form(params, additionalParams) {
        console.log("[claim_form]");
        // update the claim form deadline
        const deadline_heading =
            params.form_options.claim_deadline_alert.claim_form;
        const deadline_parser = new DOMParser();
        const deadline_string = deadline_parser.parseFromString(
            deadline_heading,
            "text/html"
        );
        const deadline_date_string = deadline_string.querySelector("span");
        // if deadline_date_string is null, then the deadline is not set
        // the if condition was added due to error in i18n translation
        // TODO: remove this if condition after fixing the i18n translation
        if (deadline_date_string !== null) {
            deadline_date_string.textContent = additionalParams;
        }

        // const the form
        const form = createElement(
            "form",
            {
                class: "needs-validation",
                id: "form",
                method: "post",
            },
            [
                createElement(
                    "h3",
                    {
                        class: "text-center mb-3",
                        id: "claimFormDeadline",
                    },
                    [],
                    deadline_string.body.innerHTML
                ),
                createElement(
                    "div",
                    {
                        class: "alert alert-info text-center fw-bold text-dark",
                        id: "confirmation-email-alert",
                        role: "alert",
                    },
                    [],
                    params.form_options.claim_form.confirmation_email_notice
                ),
                this.claim_instructions(params),
                this.claim_contact_information(params),
                this.claim_fieldset(params),
                params.claim_transactions
                    ? this.claim_transactions(params)
                    : ``,
                params.claim_upload.documentation.required
                    ? this.claim_documentation(params)
                    : ``,
                appPayMethod ? DigitalDisbursements(params) : ``,
                this.claim_certification(params),
            ]
        );
        return form;
    }

    claim_instructions(params) {
        if (
            params.form_options.claim_form.general_instructions.display ===
            false
        ) {
            return createElement("div", { class: "d-none" });
        }
        return createElement(
            "div",
            {
                class: "mb-3",
                id: "generalInstructionsWrapper",
            },
            [
                createElement(
                    "div",
                    {
                        class: "text-center fw-bold fs-3",
                    },
                    [
                        createElement(
                            "button",
                            {
                                class: "btn btn-link fs-4 fw-bold",
                                style: "color: #128f76; text-decoration: underline;",
                                type: "button",
                                "data-bs-toggle": "collapse",
                                "data-bs-target": "#generalInstructions",
                                "aria-expanded": false,
                                "aria-controls": "generalInstructions",
                            },
                            [],
                            params.form_options.claim_form.general_instructions
                                .title
                        ),
                    ]
                ),
                createElement(
                    "div",
                    {
                        class: "collapse",
                        id: "generalInstructions",
                    },
                    [
                        createElement(
                            "div",
                            {
                                class: "bg-light text-dark border rounded p-3 mb-3",
                            },
                            [],
                            params.form_options.claim_form.general_instructions
                                .body
                        ),
                    ]
                ),
            ]
        );
    }

    /**
     * Creates a contact information section for a claim form.
     * @param {Object} params - The parameters for the function.
     * @param {Object} params.form_options - The options for the claim form.
     * @returns {HTMLElement} The contact information section as an HTML element.
     * @since 3.0.0
     */
    claim_contact_information(params) {
        const contactInfoBodyDisplayBool = Boolean(
            params.form_options.claim_form.contact_information.body
        )
            ? true
            : false;
        const div = createElement(
            "div",
            {
                class: "bg-light text-dark border rounded p-3 mb-5",
                id: "contact-information",
            },
            [
                createElement(
                    "div",
                    { class: "h3 mb-3 formTitle", id: "contactInfoTitle" },
                    [],
                    params.form_options.claim_form.contact_information.title
                ),
                contactInfoBodyDisplayBool
                    ? createElement(
                          "div",
                          { class: "mb-3 formBody", id: "contactInfoBody" },
                          [],
                          params.form_options.claim_form.contact_information
                              .body
                      )
                    : ``,
                this.claim_contact_fields(params),
            ]
        );
        return div;
    }

    claim_contact_fields(params) {
        const parent = createElement("div", {
            class: "row row-cols-1 row-cols-md-2",
        });
        let fieldID = 0;
        params.contact_information.forEach((field) => {
            let validation;
            const d_none =
                field.name === "province" || field.name === "postal_code"
                    ? " d-none"
                    : "";

            const input = FormFields(
                field,
                "",
                fieldID,
                input,
                validation,
                parent
            );
            input.setAttribute("data-contact-label", field.label);
            input.removeAttribute("data-label");

            const div = createElement(
                "div",
                {
                    class: "col mb-3 divContactField" + d_none,
                    id: "divContactField_" + fieldID,
                },
                [
                    createElement(
                        "label",
                        {
                            for: field.name,
                        },
                        [],
                        `${field.label}${field.required ? "*" : ""} <span class="error"></span>`
                    ),
                    input,
                ]
            );
            parent.appendChild(div);
            fieldID++;
        });
        return parent;
    }

    /**
     * Creates a fieldset for the claim form based on the provided parameters.
     * @param {Object} params - The parameters for the claim form.
     * @param {Array} params.claim_fields - The fields for the claim form.
     * @param {Object} params.form_options - The options for the claim form.
     * @returns {HTMLElement} The fieldset element for the claim form.
     */
    claim_fieldset(params) {
        if (params.claim_fields === undefined) {
            return createElement("div", { class: "d-none" });
        }
        if (params.claim_fields.length === 0) {
            return createElement("div", { class: "d-none" });
        }
        const title = params.form_options.claim_form.claim_fields.title;
        const body = params.form_options.claim_form.claim_fields.body;
        const claimFields = createElement(
            "div",
            {
                class: "bg-light text-dark border rounded p-3 mb-5",
                id: "claim-fields",
            },
            [
                createElement(
                    "div",
                    { class: "h3 mb-3 formTitle", id: "claimFieldsTitle" },
                    [],
                    title
                ),
                createElement(
                    "div",
                    { class: "mb-3 formBody", id: "claimFieldsBody" },
                    [],
                    body
                ),
                this.claim_fields(params),
            ]
        );
        return claimFields;
    }

    claim_fields(params) {
        const container = createElement("div");

        let fieldID = 0; //
        params.claim_fields.forEach((field) => {
            let element;
            let validation;
            const className = field.className ? field.className : ``;

            const div = createElement("div", {
                class: field.parentClass ? field.parentClass : "mb-3 _wrapper",
                "data-field-id":
                    "_" + (field.name ? field.name : "_wrapper_" + fieldID),
            });
            element = FormFields(
                field,
                className,
                fieldID,
                element,
                validation,
                container
            );
            FormFieldsLabel(field, fieldID, div);

            div.append(element);
            container.appendChild(div);
            fieldID++;
        });

        return container;
    }

    claim_transactions(params) {
        if (params.claim_transactions === undefined) {
            return createElement("div", { class: "d-none" });
        }
        if (params.claim_transactions.length === 0) {
            return createElement("div", { class: "d-none" });
        }

        const fieldset = createElement(
            "div",
            {
                class: "bg-light text-dark border rounded p-3 mb-5",
                id: "transactions",
            },
            [
                createElement(
                    "div",
                    {
                        class: "h3 mb-3 formTitle",
                    },
                    [],
                    params.form_options.transactions.title
                ),
                createElement(
                    "div",
                    {
                        class: "mb-3 formBody",
                    },
                    [],
                    params.form_options.transactions.body
                ),
            ]
        );

        // For Each Transaction Table
        const claim_transactions = params.claim_transactions;
        let tableID = 0;
        claim_transactions.forEach((table) => {
            tableID++; // Begin count at 1.

            // <div class="table-responsive"></div>
            const divResponsiveTable = createElement("div", {
                class: "table-responsive mb-3",
                id: "table_responsive_" + tableID,
            });

            // <table class="table transactions"></table>
            const tableTransaction = createElement(
                "table",
                {
                    class: "table table-hover transactions caption-top",
                    id: "transactions_" + tableID,
                    "data-table-name": table.name,
                    "data-table-id": tableID,
                },
                [
                    createElement(
                        "caption",
                        { class: "text-dark bg-transparent" },
                        [],
                        "<strong>" +
                            table.name +
                            "</strong> &mdash; " +
                            table.description
                    ),
                ]
            );
            divResponsiveTable.appendChild(tableTransaction);

            // <thead class="bg-dark text-light"></thead>
            const tableTHEAD = createElement("thead", {
                class: "table-dark text-light",
            });
            tableTransaction.appendChild(tableTHEAD);

            // <tr> ...Table Data Cells... </tr>
            const theadTR = createElement("tr");
            tableTHEAD.appendChild(theadTR);

            // <th scope="col"></th>
            table.columns.forEach((col) => {
                const column = createElement(
                    "th",
                    { scope: "col" },
                    [],
                    col.title
                );
                theadTR.appendChild(column);

                if (Boolean(col.body)) {
                    const helper = createElement(
                        "span",
                        {
                            class: "d-block fw-normal",
                            style: "font-size:16px;",
                        },
                        [],
                        col.body
                    );
                    column.appendChild(helper);
                }
            });

            // <tbody></tbody>
            const tableTBODY = createElement("tbody");
            tableTransaction.appendChild(tableTBODY);

            // Add rows through a function, so we can attach a button.
            const countRow = 1;
            this.claim_transaction_row(table, tableTBODY, tableID, countRow);

            const add_row = createElement(
                "div",
                {
                    class: "text-center",
                },
                [
                    createElement(
                        "a",
                        {
                            class: "btn btn-primary m-1",
                            id: "add_transactions_" + tableID,
                            role: "button",
                            tabindex: "0",
                            "aria-label":
                                i18n.getTranslation("transaction_add_row") +
                                table.name,
                            tableID: tableID,
                        },
                        [],
                        `<i class="bi bi-plus-circle" tableID="${tableID}"></i> ${table.name}`
                    ),
                    createElement(
                        "a",
                        {
                            // ada compliance updates - added
                            class: "btn btn-danger m-1 disabled", // default to disabled
                            id: "remove_transactions_" + tableID,
                            role: "button",
                            tabindex: "0",
                            "aria-label": i18n.getTranslation(
                                "transaction_delete_row"
                            ),
                            tableID: tableID,
                        },
                        [],
                        `<i class="bi bi-dash-circle" tableID="${tableID}"></i> ${i18n.getTranslation("transaction_delete_row")}`
                    ),
                ]
            );

            // ada compliance updates - added
            this.claim_transaction_row_buttons_events(
                add_row,
                table,
                tableTBODY,
                tableID,
                countRow
            );

            // Append the table to the fieldset
            fieldset.appendChild(divResponsiveTable);
            fieldset.appendChild(add_row);
        });

        return fieldset;
    }

    /**
     * claim_transaction_row_buttons_events()
     *
     * Adds event listeners to the add and remove buttons of a claim
     * transaction row.
     *
     * ada compliance updates - added
     *
     * @param {Element} add_row - The add button element.
     * @param {Element} table - The table element.
     * @param {Element} tableTBODY - The table body element.
     * @param {string} tableID - The ID of the table.
     * @param {number} countRow - The current number of rows in the table.
     */
    claim_transaction_row_buttons_events(
        add_row,
        table,
        tableTBODY,
        tableID,
        countRow
    ) {
        // Add row for every click
        add_row.firstChild.addEventListener("click", (event) => {
            countRow++;
            const table_id = event.target.attributes.tableID.value;
            this.claim_transaction_row(table, tableTBODY, table_id, countRow);
            if (countRow > 1) {
                document
                    .querySelector(`#remove_transactions_${table_id}`)
                    .classList.remove("disabled");
            }
        });
        // Remove row for every click
        add_row.lastChild.addEventListener("click", (event) => {
            countRow--;
            const table_id = event.target.attributes.tableID.value;
            const table = document.querySelector(`#transactions_${table_id}`);
            const table_rows = table.rows.length;
            const table_tbody = table.querySelector("tbody");
            table_tbody.removeChild(table_tbody.lastChild);
            if (table_rows === 3) {
                document
                    .querySelector(`#remove_transactions_${table_id}`)
                    .classList.add("disabled");
            }
        });
    }

    /**
     * Adds a new transaction row to the given table.
     * @param {HTMLTableElement} table - The table to add the row to.
     * @param {HTMLTableSectionElement} tableTBODY - The table body to add the row to.
     * @param {string} tableID - The ID of the table.
     * @param {number} countRow - The count of the row.
     * @returns {Object} An object containing the table body and the fields row.
     */
    claim_transaction_row(table, tableTBODY, tableID, countRow) {
        const fieldsRow = createElement("tr", {
            class: "transaction-row", // ada compliance updates - added
            id: "transaction_row_" + tableID + "_" + countRow, // ada compliance updates - added
        });
        tableTBODY.appendChild(fieldsRow);

        let fieldID = 0;
        table.fields.forEach((field) => {
            fieldID++;
            const td = createElement("td", {
                class: "transaction " + field.name,
            });
            const element = createElement("div");
            const validation = field.validation;

            const formInput = FormFields(
                field,
                "",
                fieldID,
                element,
                validation,
                fieldsRow
            );

            // set unique name for each field
            field.name = field.name + "__" + countRow;
            FormFieldsLabel(field, fieldID, element);

            // set unique id for each field
            formInput.id = formInput.id + "__" + countRow;
            // set item-id attribute
            formInput.setAttribute("item-id", countRow);

            element.appendChild(formInput);
            td.appendChild(element);
            fieldsRow.appendChild(td);
        });
        return { tableTBODY, fieldsRow };
    }

    claim_documentation(params) {
        if (Boolean(params.claim_upload.documentation.required)) {
            const documentation = params.claim_upload.documentation;
            const fieldset = createElement(
                "div",
                {
                    class: "bg-light text-dark border rounded p-3 mb-5",
                    id: "documentation",
                },
                [],
                '<div class="h3 mb-3 formTitle">' +
                    documentation.title +
                    "</div>"
            );

            const fieldsetLead = createElement(
                "p",
                { class: "mb-3" },
                [],
                documentation.body
            );
            fieldset.appendChild(fieldsetLead);

            const notice = createElement(
                "p",
                { class: "mb-3" },
                [],
                documentation.file_types_notice
            );
            fieldset.appendChild(notice);
            const parInstructions = createElement(
                "p",
                { class: "text-center fw-bold mb-3" },
                [],
                documentation.instructions
            );
            fieldset.appendChild(parInstructions);

            // Documentation Inputs
            const labelFileUpload = createElement(
                "label",
                {
                    for: "file-upload",
                },
                [],
                `${documentation.label_file_upload}: <span class=\"error\"></span><span class=\"warning\"></span>`
            );
            const inputFileUpload = createElement("input", {
                type: "file",
                id: "file-upload",
                class: "form-control mb-3",
                name: "file-upload",
                "data-input": documentation.data_input,
                "data-validate": documentation.data_validate,
                "data-file-size": documentation.data_file_size,
                "data-file-type": documentation.data_file_type,
                accept: documentation.accept,
            });
            const inputFileHash = createElement("input", {
                type: "hidden",
                id: "file-hash",
            });
            const groupFileUpload = createElement(
                "div",
                {
                    class: "mb-3",
                },
                [labelFileUpload, inputFileUpload, inputFileHash]
            );
            fieldset.appendChild(groupFileUpload);

            const labelFileDesc = createElement(
                "label",
                {
                    for: "file-description",
                },
                [],
                `${documentation.label_file_desc}: <span class="error"></span>`
            );
            const inputFileDescText = createElement("input", {
                type: "text",
                class: "form-control",
                id: "file-description",
                name: "file-description",
            });
            const inputFileDescBtn = createElement("input", {
                type: "button",
                class: "btn btn-primary",
                id: "file-add",
                value: "Add File",
            });
            const inputGroup = createElement(
                "div",
                {
                    class: "input-group input-group-lg mb-3",
                },
                [inputFileDescText, inputFileDescBtn]
            );
            const groupFileDesc = createElement(
                "div",
                {
                    id: "groupFileDesc",
                },
                [labelFileDesc, inputGroup]
            );
            fieldset.appendChild(groupFileDesc);

            const fileListPar = createElement(
                "p",
                {
                    id: "fileList",
                },
                [],
                `<strong>File List:</strong> <span id="no-files">No Files Selected</span>`
            );

            const fileOrderedList = createElement("ol", {
                class: "d-none",
                id: "file-list",
            });
            const fileListElem = createElement(
                "div",
                {
                    id: "fileListElem",
                },
                [fileListPar, fileOrderedList]
            );
            fieldset.appendChild(fileListElem);

            return fieldset;
        }
    }

    claim_certification(params) {
        const today = new Date().toLocaleString();
        const _ag_uuid = document.cookie
            .split("; ")
            .find((row) => row.startsWith("_ag_UUID="))
            ?.split("=")[1];
        const _ag_referrer = document.cookie
            .split("; ")
            .find((row) => row.startsWith("_ag_URL="))
            ?.split("=")[1];
        const _ag_sguid = localStorage.getItem("_ag_SGUID")
            ? localStorage.getItem("_ag_SGUID")
            : "";
        const _ag_TEST = localStorage.getItem("_ag_TEST")
            ? localStorage.getItem("_ag_TEST")
            : "";

        const fieldset = createElement(
            "div",
            {
                class: "bg-light text-dark border rounded p-3 mb-5",
                id: "certification",
            },
            [
                createElement(
                    "div",
                    { class: "h3 mb-3 formTitle" },
                    [],
                    params.certification.attestation.title
                ),
                createElement(
                    "div",
                    { class: "mb-3" },
                    [],
                    params.certification.attestation.body
                ),
                createElement(
                    "div",
                    {
                        class: "alert alert-info border-info bg-info-subtle text-info-emphasis mb-4 px-4 py-3",
                        role: "region",
                        "aria-labelledby": "ueta-notice-heading",
                        "aria-describedby": "ueta-notice-text",
                    },
                    [
                        createElement(
                            "div",
                            {
                                class: "d-flex align-items-start gap-3",
                            },
                            [
                                createElement("i", {
                                    class: "bi bi-info-circle-fill text-info fs-5 flex-shrink-0 mt-1",
                                    "aria-hidden": "true",
                                }),
                                createElement(
                                    "div",
                                    {
                                        class: "flex-grow-1",
                                    },
                                    [
                                        createElement(
                                            "h6",
                                            {
                                                class: "fw-bold mb-2 text-info-emphasis",
                                                id: "ueta-notice-heading",
                                            },
                                            [],
                                            i18n.getTranslation(
                                                "signature_ueta_title"
                                            )
                                        ),
                                        createElement(
                                            "p",
                                            {
                                                class: "mb-0 small lh-sm",
                                                id: "ueta-notice-text",
                                            },
                                            [],
                                            i18n.getTranslation(
                                                "signature_ueta_compliant"
                                            )
                                        ),
                                    ]
                                ),
                            ]
                        ),
                    ]
                ),
                createElement("div", { class: "row" }, [
                    createElement("div", { class: "col-md-5" }, [
                        createElement("input", {
                            type: "text",
                            name: "signature",
                            class: "form-control",
                            id: "signature",
                            "data-validate": "required",
                            "data-contact-label":
                                i18n.getTranslation("signature"),
                            placeholder: i18n.getTranslation(
                                "signature_placholder"
                            ),
                            required: "", // ada compliance updates - added
                        }),
                        createElement(
                            "label",
                            { for: "signature" },
                            [],
                            `${i18n.getTranslation("signature")}* <span class="error"></span>`
                        ),
                    ]),
                    createElement("div", { class: "col-md-5" }, [
                        createElement("input", {
                            type: "text",
                            name: "date",
                            class: "form-control",
                            id: "date",
                            "data-validate": "required",
                            "data-contact-label": "Date",
                            disabled: "disabled",
                            value: today,
                        }),
                        createElement(
                            "label",
                            { for: "date" },
                            [],
                            `${i18n.getTranslation("date")} <span class="error"></span>`
                        ),
                    ]),
                    createElement(
                        "div",
                        {
                            class: "col-md-2",
                        },
                        [
                            createElement(
                                "div",
                                {
                                    class: "d-grid",
                                },
                                [
                                    createElement("input", {
                                        type: "hidden",
                                        id: "site_id",
                                        value: reCAPTCHAv3,
                                    }),
                                    createElement("input", {
                                        type: "hidden",
                                        name: "ag_UUID",
                                        id: "ag_UUID",
                                        value: _ag_uuid,
                                    }),
                                    createElement("input", {
                                        type: "hidden",
                                        name: "referer_url",
                                        id: "referer_url",
                                        value: _ag_referrer,
                                    }),
                                    createElement("input", {
                                        type: "hidden",
                                        name: "user_language",
                                        id: "user_language",
                                        value: userLanguage,
                                    }),
                                    createElement("input", {
                                        type: "hidden",
                                        name: "ag_SGUID",
                                        id: "ag_SGUID",
                                        value: _ag_sguid,
                                    }),
                                    Boolean(_ag_TEST)
                                        ? createElement("input", {
                                              type: "hidden",
                                              name: "ag_TEST",
                                              id: "ag_TEST",
                                              value: _ag_TEST,
                                          })
                                        : ``,
                                    createElement("input", {
                                        type: "submit",
                                        class: "btn btn-primary",
                                        id: "submit-claim",
                                        value: i18n.getTranslation(
                                            "submit_btn"
                                        ),
                                    }),
                                ]
                            ),
                        ]
                    ),
                ]),
            ]
        );
        return fieldset;
    }

    claim_confirmation(params) {
        console.log("[claim_confirmation]");
        const confirmation = createElement(
            "div",
            {
                class: "bg-light text-dark border rounded p-3 mb-3",
                id: "confirmation",
            },
            [
                createElement(
                    "h3",
                    { class: "text-center mb-3" },
                    [],
                    params.confirmation["title"]
                ),
                createElement(
                    "h4",
                    { class: "alert alert-success text-center mb-3" },
                    [],
                    i18n.getTranslation("confirmation_this_is_a_copy")
                ),
                createElement(
                    "p",
                    { class: "text-center text-danger mb-3" },
                    [],
                    params.confirmation.upload_fail
                ),
                createElement(
                    "p",
                    { class: "text-center fw-bold mb-3" },
                    [],
                    params.confirmation.print_note
                ),
                createElement(
                    "h4",
                    { class: "text-center fw-bold mb-3" },
                    [],
                    params.confirmation.claim_details.title
                ),
                createElement(
                    "p",
                    { class: "text-center fw-bold mb-3" },
                    [],
                    params.confirmation.claim_details.body
                ),
                this.confirmation_claim_member(params),
                this.confirmation_claim_fields(params),
                this.confirmation_claim_transactions(params),
                createElement(
                    "div",
                    {
                        class: "row mb-3",
                    },
                    [
                        createElement(
                            "div",
                            { class: "col-6 text-end fw-bold" },
                            [],
                            "Correlation ID"
                        ),
                        createElement(
                            "div",
                            {
                                class: "col-6",
                                id: "confirmation_correlation_id",
                            },
                            [],
                            params.confirmation.claim_details.correlation_id
                        ),
                    ]
                ),
                createElement(
                    "p",
                    { class: "text-center mb-3" },
                    [],
                    params.confirmation.claim_details.footer
                ),
                createElement(
                    "p",
                    { class: "text-center mb-3" },
                    [],
                    params.confirmation.claim_details.edit
                ),
            ]
        );
        return confirmation;
    }

    exclusion_confirmation(params) {
        console.log("[exclusion_confirmation]");
        const confirmation = createElement(
            "div",
            {
                class: "bg-light text-dark border rounded p-3 mb-3",
                id: "confirmation",
            },
            [
                createElement(
                    "h3",
                    { class: "text-center mb-3" },
                    [],
                    params.confirmation.title
                ),
                createElement(
                    "p",
                    { class: "text-center text-danger mb-3" },
                    [],
                    params.confirmation.upload_fail
                ),
                createElement(
                    "p",
                    { class: "text-center fw-bold mb-3" },
                    [],
                    params.confirmation.print_note
                ),
                createElement(
                    "h4",
                    { class: "text-center fw-bold mb-3" },
                    [],
                    params.confirmation.claim_details.title
                ),
                createElement(
                    "p",
                    { class: "text-center fw-bold mb-3" },
                    [],
                    params.confirmation.claim_details.body
                ),
                this.confirmation_claim_member(params),
                this.confirmation_claim_fields(params),
                this.confirmation_claim_transactions(params),
                createElement(
                    "p",
                    { class: "text-center mb-3" },
                    [],
                    params.confirmation.claim_details.footer
                ),
                createElement(
                    "p",
                    { class: "text-center mb-3" },
                    [],
                    params.confirmation.claim_details.edit
                ),
            ]
        );
        return confirmation;
    }

    confirmation_claim_member(params) {
        console.log("[confirmation_claim_member]");
        const fields = params.contact;
        const fieldsContainer = createElement("div", {
            class: "mb-3",
            id: "confirmation_claim_member",
        });
        Object.keys(fields).forEach((key) => {
            if (
                fields[key] &&
                document.getElementById(key).getAttribute("data-contact-label")
            ) {
                const row = createElement(
                    "div",
                    {
                        class: "row mb-3 pb-3 border-bottom",
                    },
                    [
                        createElement(
                            "div",
                            { class: "col fw-bold text-end" },
                            [],
                            document
                                .getElementById(key)
                                .getAttribute("data-contact-label")
                        ),
                        createElement("div", { class: "col" }, [], fields[key]),
                    ]
                );
                fieldsContainer.appendChild(row);
            }
        });
        return fieldsContainer;
    }

    confirmation_claim_fields(params) {
        console.log("[confirmation_claim_fields]");
        const fields = params.fields;
        const fieldsContainer = createElement("div", {
            class: "mb-3",
            id: "confirmation_claim_fields",
        });
        Object.keys(fields).forEach((key) => {
            const fieldLabel = i18n.getField(key);
            if (
                !params.confirmation.claim_details.excluded_keys.includes(key)
            ) {
                if (fields[key]) {
                    const row = createElement(
                        "div",
                        {
                            class: "row mb-3 pb-3 border-bottom",
                        },
                        [
                            createElement(
                                "div",
                                { class: "col fw-bold text-end" },
                                [],
                                fieldLabel
                            ),
                            createElement(
                                "div",
                                { class: "col" },
                                [],
                                fields[key]
                            ),
                        ]
                    );
                    fieldsContainer.appendChild(row);
                }
            }
        });
        return fieldsContainer;
    }

    confirmation_claim_transactions(params) {
        const transactions = params.transactions;
        const fieldsContainer = createElement("div", {
            class: "mb-3",
            id: "confirmation_claim_transactions",
        });
        Object.keys(transactions).forEach((index) => {
            Object.keys(transactions[index]).forEach((key) => {
                const fieldLabel = i18n.getField(key);
                if (transactions[index][key]) {
                    const row = createElement(
                        "div",
                        {
                            class: "row mb-3 pb-3 border-bottom",
                        },
                        [
                            createElement(
                                "div",
                                { class: "col fw-bold text-end" },
                                [],
                                `${fieldLabel} ${index}`
                            ),
                            createElement(
                                "div",
                                { class: "col" },
                                [],
                                transactions[index][key]
                            ),
                        ]
                    );
                    fieldsContainer.appendChild(row);
                }
            });
        });
        return fieldsContainer;
    }

    confirmation_tr_cols(params) {
        const row = createElement("tr");
        params.columns.forEach((column) => {
            const th = createElement("th", {}, [], column.title);
            row.appendChild(th);
        });
        return row;
    }

    confirmation_claim_files(params) {
        console.log(params);
        const files = params.files;
        const filesContainer = createElement("div", {
            class: "mb-3",
            id: "confirmation_claim_files",
        });
        files.forEach(function (file, index) {
            const row = createElement(
                "div",
                {
                    class: "row mb-3 pb-3 border-bottom",
                },
                [
                    createElement(
                        "div",
                        { class: "col fw-bold text-end" },
                        [],
                        `File ${index + 1}`
                    ),
                    createElement(
                        "div",
                        { class: "col" },
                        [],
                        file.name + " - " + file.description
                    ),
                ]
            );
            filesContainer.appendChild(row);
        });
        return filesContainer;
    }

    claim_message_center() {
        return createElement(
            "div",
            {
                id: "messageContainer",
            },
            [
                createElement("div", { class: "d-none", id: "message1" }),
                createElement("div", { class: "d-none", id: "message2" }),
            ]
        );
    }

    setup_phone_number_validation() {
        const phoneInputs = document.querySelectorAll(
            '[data-validate~="phone"]'
        );
        phoneInputs.forEach((input) => {
            input.addEventListener("keyup", formatPhone);
        });
    }

    setup_country_field_toggle() {
        const countrySelect = document.getElementById("country");

        const toggleFieldsBasedOnCountry = () => {
            const usaSelected = countrySelect.value === "USA";

            const fieldsToToggle = [
                { selector: "#state,#zip_code", required: usaSelected },
                { selector: "#province,#postal_code", required: !usaSelected },
            ];

            fieldsToToggle.forEach(({ selector, required }) => {
                const elements = document.querySelectorAll(selector);
                elements.forEach((input) => {
                    input.value = required ? input.value : "";
                    toggleFieldAttributeV2(input, "required", required);
                    input.parentElement.classList.toggle("d-none", !required);
                });
            });

            toggleForeignPhone(countrySelect.value);
        };

        countrySelect.addEventListener("change", toggleFieldsBasedOnCountry);
    }

    setup_result_data_error(result) {
        const div = createElement(
            "div",
            {
                class: "text-center text-danger fw-bold fs-4 py-4",
                "aria-label": result.data.error,
            },
            [],
            `${result.data.error} <br> ${i18n.getTranslation("return_to_home")}`
        );

        outputDiv.innerHTML = ``;
        outputDiv.appendChild(div);

        if (document.querySelector("#loading").classList.contains("show")) {
            loadingGif(loading, "hide", 250);
        }
    }
}
